import { z } from 'zod';
import { Rule, Severity } from '@/rule/domain/Rule.ts';
import { jsonToPage, type Page, pageSchemaOf } from '@/generic/pagination';
import { Optional } from '@/generic/functional';
import { Categories } from '@/rule/domain/Categories.ts';
import { Example, ExampleCompliance } from '@/rule/domain/Example.ts';
import { Examples } from '@/rule/domain/Examples.ts';
import { RuleStatus } from '@/rule/domain/RuleStatus.ts';

export const exampleSchema = z.object({
  description: z.string(),
  snippet: z.string(),
  compliance: z.enum(['COMPLIANT', 'NON_COMPLIANT']),
});

type CleanApiExampleJson = z.infer<typeof exampleSchema>;

export const severitySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);

export type CleanApiSeverityJson = z.infer<typeof severitySchema>;

export const statusSchema = z.enum(['ACTIVE', 'INACTIVE']);

export type CleanApiStatusJson = z.infer<typeof statusSchema>;

const ruleSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  severity: severitySchema,
  status: statusSchema,
  examples: z.array(exampleSchema),
  organizationId: z.string().optional(),
});

export type CleanApiRuleJson = z.infer<typeof ruleSchema>;

export const rulesPageSchema = pageSchemaOf(ruleSchema);

export type CleanApiRulesJson = z.infer<typeof rulesPageSchema>;

export const jsonToSeverity = (severity: CleanApiSeverityJson): Severity => {
  switch (severity) {
    case 'CRITICAL':
      return Severity.CRITICAL;
    case 'HIGH':
      return Severity.HIGH;
    case 'MEDIUM':
      return Severity.MEDIUM;
    case 'LOW':
      return Severity.LOW;
  }
};

export const jsonToStatus = (status: CleanApiStatusJson): RuleStatus => {
  switch (status) {
    case 'ACTIVE':
      return RuleStatus.ACTIVE;
    case 'INACTIVE':
      return RuleStatus.INACTIVE;
  }
};

const jsonToRule = (ruleJson: CleanApiRuleJson): Rule =>
  Rule.of({
    id: ruleJson.id,
    title: ruleJson.title,
    description: Optional.ofFalsifiable(ruleJson.description),
    severity: jsonToSeverity(ruleJson.severity),
    categories: Categories.of(),
    status: jsonToStatus(ruleJson.status),
    examples: Examples.of(...ruleJson.examples.map(jsonToExample)),
    organizationId: Optional.ofUndefinable(ruleJson.organizationId),
  });

const jsonToCompliance = (compliance: CleanApiExampleJson['compliance']): ExampleCompliance => {
  switch (compliance) {
    case 'COMPLIANT':
      return ExampleCompliance.COMPLIANT;
    case 'NON_COMPLIANT':
      return ExampleCompliance.NON_COMPLIANT;
  }
};

const jsonToExample = (json: CleanApiExampleJson): Example =>
  Example.of({
    snippet: json.snippet,
    compliance: jsonToCompliance(json.compliance),
    description: json.description,
  });

export const parseJsonToRulesPage = (json: unknown): Page<Rule> =>
  jsonToPage(rulesPageSchema.parse(json), jsonToRule);

export const parseJsonToRule = (json: unknown): Rule => jsonToRule(ruleSchema.parse(json));

export const statusToJson = (status: RuleStatus): CleanApiStatusJson => {
  switch (status) {
    case RuleStatus.ACTIVE:
      return 'ACTIVE';
    case RuleStatus.INACTIVE:
      return 'INACTIVE';
  }
};

export const severityToJson = (severity: Severity): CleanApiSeverityJson => {
  switch (severity) {
    case Severity.CRITICAL:
      return 'CRITICAL';
    case Severity.HIGH:
      return 'HIGH';
    case Severity.MEDIUM:
      return 'MEDIUM';
    case Severity.LOW:
      return 'LOW';
    case Severity.INFO:
      throw new Error('A rule cannot be filtered on the info severity');
  }
};
